/**
 * Fires the adaptive submit POST after the student finishes the last
 * module, then navigates to the existing SHSAT report screen.
 *
 * The endpoint is idempotent (re-POSTing returns the existing score),
 * so the AbortController + Strict-Mode double-mount is safe. On
 * success we delegate to ``/report/digital-shsat/<studentTestId>``
 * — backend ``DigitalResultSHSATView`` is now adaptive-aware, so the
 * legacy SHSAT report UI renders for adaptive tests verbatim. Mirroring
 * the existing UI is intentional; see
 * ``memory/feedback_shsat_adaptive_style_mirror.md``.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import axios from 'config/axios';
import Loader from 'components/Loader';
import Button from 'components/Button';


export function AdaptiveSubmitGate(props: {
  studentTestId: string;
}): JSX.Element {
  const { studentTestId } = props;
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const inflightRef = useRef<AbortController | null>(null);

  const submit = async () => {
    inflightRef.current?.abort();
    const controller = new AbortController();
    inflightRef.current = controller;
    setErrorMessage(null);
    try {
      await axios.post(
        `tests/digital/shsat/adaptive/submit/${studentTestId}/`,
        {},
        { signal: controller.signal },
      );
      if (controller.signal.aborted) return;
      navigate(`/report/digital-shsat/${studentTestId}`, { replace: true });
    } catch (err) {
      if (controller.signal.aborted) return;
      const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
      setErrorMessage(
        anyErr.response?.data?.message ??
        anyErr.message ??
        'Failed to submit your test. Please try again.',
      );
    }
  };

  useEffect(() => {
    submit();
    return () => {
      inflightRef.current?.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-once
  }, [studentTestId]);

  if (errorMessage) {
    return (
      <div style={{ maxWidth: 720, margin: '60px auto', padding: 20, textAlign: 'center' }}>
        <h2>Something went wrong</h2>
        <p>{errorMessage}</p>
        <Button type="button" onClick={submit}>Retry</Button>
      </div>
    );
  }

  return <Loader />;
}
